<template>
  <ul
    v-if="selectedNodes.length"
    class="cn-treeview-filter"
    :class="{ 'cn-treeview-filter--disabled': disabled }"
  >
    <li
      v-for="node in selectedNodes"
      :key="node.id"
      class="cn-treeview-filter__item"
    >
      <span class="cn-treeview-filter__label">{{ node.name }}</span>
      <button
        type="button"
        class="cn-treeview-filter__remove"
        :aria-label="node.name"
        @click="remove(node.id)"
      >
        <UiIcon name="close" />
      </button>
    </li>
  </ul>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
  flatten,
  type TreeviewId,
  type TreeviewSource,
} from '@/components/ui/treeview';
import UiIcon from '@/components/ui/UiIcon';

interface Props {
  items?: TreeviewSource[];
  modelValue: TreeviewId[];
  disabled: boolean;
}

const props = withDefaults(defineProps<Props>(), { items: () => [] });
const emit = defineEmits<{
  (e: 'update:modelValue', value: TreeviewId[]): void;
}>();

const selectedNodes = computed(() => {
  const byId = new Map(flatten(props.items).map((node) => [node.id, node]));
  return props.modelValue
    .map((id) => byId.get(id))
    .filter((node): node is TreeviewSource => !!node);
});

const remove = (id: TreeviewId) => {
  emit(
    'update:modelValue',
    props.modelValue.filter((value) => value !== id),
  );
};
</script>

<style lang="scss">
.cn-treeview-filter {
  --ab-canyon-treeview-filter-item-color-bg: var(--ab-canyon-color-accent-2);
  --ab-canyon-treeview-filter-item-color-text: var(--ab-canyon-color-dark);
  --ab-canyon-treeview-filter-remove-color-text: var(
    --ab-canyon-color-neutral-dark
  );

  row-gap: var(--size-base);
  flex-wrap: wrap;
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;

  &--disabled {
    opacity: 0.6;
    pointer-events: none;
  }

  // vue-treeselect built the chip as an inline-table, so its 2px border-spacing
  // sat between the label and the remove button and around the whole box —
  // hence the gap here and the 6px of vertical padding instead of 5px
  &__item {
    background: var(--ab-canyon-treeview-filter-item-color-bg);
    color: var(--ab-canyon-treeview-filter-item-color-text);
    margin-right: var(--size-base);
    align-items: center;
    line-height: 1.5;
    font-size: 14px;
    padding: 6px 0;
    display: flex;
    gap: 2px;
  }

  &__label {
    padding: 0 calc(var(--size-base) / 2);
  }

  &__remove {
    color: var(--ab-canyon-treeview-filter-remove-color-text);
    padding: 0 calc(var(--size-base) / 2);
    line-height: 0;

    .cn-icon {
      width: 6px;
      height: 6px;
    }
  }
}
</style>
